import React, { useEffect, useState } from "react";
import styles from "./FidelityDashboard.module.scss";
import { 
  Card, 
  Row, 
  Col, 
  Typography, 
  Statistic, 
  DatePicker, 
  Space,
  Progress,
  List,
  Avatar,
  Tag,
  Spin,
  Button
} from "antd";
import { 
  UserOutlined, 
  ShoppingOutlined, 
  GiftOutlined, 
  TrophyOutlined,
  RiseOutlined,
  DollarOutlined,
  CalendarOutlined,
  StarOutlined,
  TeamOutlined,
  BarChartOutlined
} from "@ant-design/icons";
import { Line, Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import moment, { Moment } from "moment";
import { DataScienceApi } from "../../../api/DataScienceApi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Legend,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Legend
);

const { RangePicker } = DatePicker;

const FidelityDashboard = () => {
  const { t } = useTranslation();
  const [dateRange, setDateRange] = useState<[Moment, Moment] | null>(null);
  const [pendingRange, setPendingRange] = useState<[Moment, Moment] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingTotal, setIsLoadingTotal] = useState<boolean>(false);
  const [isLoadingCompare, setIsLoadingCompare] = useState<boolean>(false);
  const [compareData, setCompareData] = useState<any[]>([]);
  const [selectedMetric, setSelectedMetric] = useState("n_clients");
  const [allClients, setAllClients] = useState<number | null>(null);
  const [activeClients, setActiveClients] = useState<number | null>(null);
  const [avgTickets, setAvgTickets] = useState<number | null>(null);
  const [avgSpending, setAvgSpending] = useState<number | null>(null);

  const metricOptions = [
    { key: "n_clients", label: "Clientes" },
    { key: "n_tickets", label: "Tickets" },
    { key: "total_spending", label: "Gasto total" },
    { key: "spending_percentage", label: "% Gasto" },
  ];

  const metricLabels: Record<string, string> = {
    n_clients: "Clientes",
    n_tickets: "Tickets",
    total_spending: "Gasto total",
    spending_percentage: "% Gasto",
  };

  const getMetricValue = (obj: any, key: string) => {
    if (!obj) return 0;
    return obj[key] ?? 0;
  };

  useEffect(() => {
    const today = moment();
    const lastMonth = today.clone().subtract(1, "month");
    setDateRange([lastMonth, today]);
    setPendingRange([lastMonth, today]);
  }, []);

  useEffect(() => {
    const organizationId = localStorage.getItem("organizationId");
    if (!organizationId) return;
    
    setIsLoadingTotal(true);
    DataScienceApi.getAllClientsKpi(organizationId)
      .then((data) => {
        if (data && data[0] && data[0].all_clients) {
          setAllClients(data[0].all_clients);
        } else {
          setAllClients(null);
        }
      })
      .catch(() => {
        setAllClients(null);
      })
      .finally(() => {
        setIsLoadingTotal(false);
      });
  }, []);

  const fetchKpis = async (range: [Moment, Moment]) => {
    try {
      setIsLoading(true);
      const organizationId = localStorage.getItem("organizationId");
      if (!organizationId) return;

      const startDate = range[0].format("YYYY-MM-DD");
      const endDate = range[1].format("YYYY-MM-DD");

      const kpiData = await DataScienceApi.getKpiActiveClients(organizationId, { startDate, endDate });
      setActiveClients(Number(kpiData[0]?.n_clients) ?? null);
      setAvgTickets(Number(kpiData[0]?.avg_tickets_per_client) ?? null);
      setAvgSpending(Number(kpiData[0]?.weigthed_avg_spending) ?? null);

      const compareData = await DataScienceApi.getProfitabilityActiveClientsRaw(organizationId, { startDate, endDate });
      setCompareData(compareData);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = () => {
    if (pendingRange) {
      setDateRange(pendingRange);
      fetchKpis(pendingRange);
    }
  };

  useEffect(() => {
    if (dateRange) {
      fetchKpis(dateRange);
    }
  }, []);

  const formatValue = (value: number | null) => {
    if (value === null) return "-";
    return value.toLocaleString();
  };

  const formatNumber = (value: number | null) => {
    if (value === null) return "-";
    return value.toFixed(2);
  };

  const formatCurrency = (value: number | null) => {
    if (value === null) return "-";
    return `$${value.toFixed(2)}`;
  };

  const getCompareValue = (data: any, key: string) => {
    if (!data || typeof data[key] === "undefined" || data[key] === null) return "-";
    if (key === "spending_percentage") return `${data[key].toFixed(2)}%`;
    if (key === "total_spending") return `$${data[key].toLocaleString(undefined, {maximumFractionDigits: 2})}`;
    return data[key].toLocaleString();
  };

  const fidelizados = compareData.find((d) => d.client_status === "fidelizados");
  const noFidelizados = compareData.find((d) => d.client_status === "no_fidelizados");

  // Mock data para gráficos que no tienen API
  const activeClientsTrendData = {
    labels: ["Jan", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dec"],
    datasets: [
      {
        label: "Clientes Activos",
        data: [20, 50, 80, 120, 180, 250, 300, 350, 400, 450, 480, 480],
        borderColor: "#3b82f6",
        backgroundColor: "rgba(59, 130, 246, 0.1)",
        tension: 0.4,
        fill: true,
        pointRadius: 4,
        pointHoverRadius: 6,
      },
    ],
  };

  const topSegmentsData = {
    labels: ["Segmento 1", "Segmento 2", "Segmento 3", "Segmento 4", "Segmento 5"],
    datasets: [
      {
        label: "Actividad",
        data: [590, 550, 500, 450, 400],
        backgroundColor: "#3b82f6",
        borderRadius: 4,
      },
    ],
  };

  const clientLevelsData = {
    labels: ["Completada", "En Tránsito", "Enviada", "Borrador"],
    datasets: [
      {
        data: [45000, 34000, 22000, 11000],
        backgroundColor: ["#fb923c", "#8b5cf6", "#3b82f6", "#ef4444"],
        borderWidth: 0,
      },
    ],
  };

  const popularRewards = [
    { name: "Descuento 10%", redemptions: 1250, points: 1000 },
    { name: "Producto Gratis", redemptions: 890, points: 2500 },
    { name: "Envío Gratis", redemptions: 2100, points: 500 },
    { name: "Cupón $50", redemptions: 750, points: 5000 },
    { name: "Producto Premium", redemptions: 320, points: 10000 }
  ];

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      y: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        },
        ticks: {
          stepSize: 100
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { 
        display: false 
      },
    },
    scales: {
      y: { 
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.05)"
        },
        ticks: {
          stepSize: 100
        }
      },
      x: {
        grid: {
          display: false
        }
      }
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
    },
  };

  return (
    <div className={styles.dashboardWrapper}>
      {/* Header con filtros */}
      <div className={styles.dashboardHeader}>
        <div className={styles.headerContent}>
          <Space size="middle">
            <RangePicker
              value={pendingRange}
              onChange={(dates) => setPendingRange(dates as [Moment, Moment])}
              allowClear={false}
              placeholder={["Inicio", "Fin"]}
            />
            <Button type="primary" onClick={handleUpdate} className={styles.updateButton}>
              Actualizar
            </Button>
          </Space>
        </div>
      </div>

      {/* KPIs Grid */}
      <div className={styles.kpisSection}>
        <Row gutter={[24, 24]}>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className={styles.kpiCard}>
              <Spin spinning={isLoadingTotal}>
                <Statistic
                  title="Clientes registrados"
                  value={formatValue(allClients)}
                  prefix={<UserOutlined className={styles.kpiIcon} />}
                  valueStyle={{ color: '#667eea' }}
                />
              </Spin>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className={styles.kpiCard}>
              <Spin spinning={isLoading}>
                <Statistic
                  title="Clientes activos"
                  value={formatValue(activeClients)}
                  prefix={<ShoppingOutlined className={styles.kpiIcon} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Spin>
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className={styles.kpiCard}>
              <Statistic
                title="Puntos acumulados"
                value="2,500,000"
                prefix={<GiftOutlined className={styles.kpiIcon} />}
                valueStyle={{ color: '#fa8c16' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className={styles.kpiCard}>
              <Statistic
                title="Puntos canjeados"
                value="180,000"
                prefix={<TrophyOutlined className={styles.kpiIcon} />}
                valueStyle={{ color: '#f5576c' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className={styles.kpiCard}>
              <Statistic
                title="Frecuencia de compras"
                value="2.3"
                prefix={<RiseOutlined className={styles.kpiIcon} />}
                valueStyle={{ color: '#13c2c2' }}
                suffix="veces/mes"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Card className={styles.kpiCard}>
              <Statistic
                title="Ticket promedio"
                value="$1,250"
                prefix={<DollarOutlined className={styles.kpiIcon} />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>
      </div>

      {/* Gráficos principales */}
      <Row gutter={[24, 24]} className={styles.chartsSection}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <BarChartOutlined />
                <span>Tendencia de clientes activos</span>
              </Space>
            }
            className={styles.chartCard}
          >
            <div className={styles.chartContainer}>
              <Line data={activeClientsTrendData} options={chartOptions} />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <StarOutlined />
                <span>Top segmentos por actividad</span>
              </Space>
            }
            className={styles.chartCard}
          >
            <div className={styles.chartContainer}>
              <Bar data={topSegmentsData} options={barChartOptions} />
            </div>
          </Card>
        </Col>
      </Row>

      {/* Gráficos secundarios */}
      <Row gutter={[24, 24]} className={styles.chartsSection}>
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <TeamOutlined />
                <span>Clientes por nivel</span>
              </Space>
            }
            className={styles.chartCard}
          >
            <div className={styles.chartContainer}>
              <Doughnut data={clientLevelsData} options={doughnutOptions} />
            </div>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card className={styles.chartCard}>
            <div className={styles.rewardsList}>
              <div style={{ marginBottom: 16 }}>
                <Typography.Text strong style={{ fontSize: '14px', color: '#666' }}>Recompensas populares</Typography.Text>
                <div style={{ marginTop: 8, height: '8px', background: 'linear-gradient(to right, #8b5cf6 0%, #a78bfa 25%, #f87171 50%, #fb923c 75%, #3b82f6 100%)', borderRadius: '4px' }}></div>
              </div>
              <div>
                <Typography.Text strong style={{ fontSize: '14px', color: '#666' }}>Actividad por tienda</Typography.Text>
                <div style={{ marginTop: 8, height: '8px', background: 'linear-gradient(to right, #8b5cf6 0%, #a78bfa 25%, #f87171 50%, #fb923c 75%, #3b82f6 100%)', borderRadius: '4px' }}></div>
              </div>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default FidelityDashboard; 